import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, Target, Calendar, TrendingUp, Sparkles, Trash2, 
  Pencil, Pause, Play, Settings, RefreshCw, Users, Wallet, AlertCircle,
  ChevronRight, Check, BarChart3, Clock
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import { 
  getObjectives, createObjective, updateObjective, deleteObjective, 
  contributeToObjective, overrideObjectiveContribution, getFamily, getObjectivesStats,
  getSuggestedContribution
} from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { useTutorial } from '../components/OnboardingTutorial';

const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial'));

const OBJECTIVE_ICONS = [
  { id: 'target', icon: Target, label: 'Cible' },
  { id: 'home', icon: '🏠', label: 'Maison' },
  { id: 'car', icon: '🚗', label: 'Voiture' },
  { id: 'vacation', icon: '🏖️', label: 'Vacances' },
  { id: 'education', icon: '🎓', label: 'Études' },
  { id: 'wedding', icon: '💍', label: 'Mariage' },
  { id: 'baby', icon: '👶', label: 'Bébé' },
  { id: 'emergency', icon: '🆘', label: 'Urgence' },
  { id: 'gift', icon: '🎁', label: 'Cadeau' },
  { id: 'tech', icon: '💻', label: 'Tech' },
];

// Custom tooltip component for savings chart
function SavingsChartTooltip({ active, payload, language }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-popover border rounded-lg p-2 shadow-lg">
        <p className="text-xs text-muted-foreground">
          {language === 'fr' ? data.month_fr : language === 'pt' ? (data.month_pt || data.month_en) : data.month_en} {data.year}
        </p>
        <p className="text-sm font-medium text-green-600">
          {t('contributed')}: {formatCurrency(data.contributed)}
        </p>
        <p className="text-sm font-bold">
          {t('total')}: {formatCurrency(data.cumulative)}
        </p>
      </div>
    );
  }
  return null;
}

export default function ObjectivesPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { shouldShow: showTutorial } = useTutorial();
  
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState(null);
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  
  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showContribute, setShowContribute] = useState(null);
  const [showOverride, setShowOverride] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    target_date: '',
    icon: 'target',
    color: '#FF4F00',
    budget_type: 'personal',
    auto_contribute: false,
    monthly_contribution: '',
    contribution_day: 1,
    notes: ''
  });
  const [contributeAmount, setContributeAmount] = useState('');
  const [overrideAmount, setOverrideAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState({}); // Store suggestions by objective_id

  // Fetch suggested contribution for an objective
  const fetchSuggestion = useCallback(async (objectiveId) => {
    try {
      const res = await getSuggestedContribution(objectiveId);
      setSuggestions(prev => ({
        ...prev,
        [objectiveId]: res.data
      }));
    } catch (error) {
      console.error('Failed to fetch suggestion:', error);
    }
  }, []);

  // Apply suggestion to form
  const applySuggestion = (objectiveId) => {
    const suggestion = suggestions[objectiveId];
    if (suggestion?.suggested_monthly_contribution) {
      setFormData(prev => ({
        ...prev,
        auto_contribute: true,
        monthly_contribution: suggestion.suggested_monthly_contribution.toString()
      }));
      toast.success(
        t('suggested_contribution_applied').replace('{amount}', suggestion.suggested_monthly_contribution)
      );
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [objRes, familyRes, statsRes] = await Promise.all([
        getObjectives(),
        user?.family_id ? getFamily() : Promise.resolve(null),
        getObjectivesStats()
      ]);
      
      setObjectives(objRes.data || []);
      if (familyRes?.data?.family) {
        setFamily(familyRes.data.family);
      }
      if (statsRes?.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to load objectives:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.family_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Show tutorial after component is mounted
  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => setTutorialActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  const resetForm = () => {
    setFormData({
      name: '',
      target_amount: '',
      target_date: '',
      icon: 'target',
      color: '#FF4F00',
      budget_type: 'personal',
      auto_contribute: false,
      monthly_contribution: '',
      contribution_day: 1,
      notes: ''
    });
  };

  const openEditModal = (obj) => {
    setFormData({
      name: obj.name,
      target_amount: obj.target_amount.toString(),
      target_date: obj.target_date ? obj.target_date.split('T')[0] : '',
      icon: obj.icon || 'target',
      color: obj.color || '#FF4F00',
      budget_type: obj.budget_type || 'personal',
      auto_contribute: obj.auto_contribute || false,
      monthly_contribution: obj.monthly_contribution?.toString() || '',
      contribution_day: obj.contribution_day || 1,
      notes: obj.notes || ''
    });
    setShowEdit(obj);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.target_amount || !formData.target_date) {
      toast.error(t('fill_required_fields'));
      return;
    }
    
    setIsSubmitting(true);
    try {
      await createObjective({
        name: formData.name,
        target_amount: parseFloat(formData.target_amount),
        target_date: new Date(formData.target_date).toISOString(),
        icon: formData.icon,
        color: formData.color,
        budget_type: formData.budget_type,
        auto_contribute: formData.auto_contribute,
        monthly_contribution: formData.auto_contribute ? parseFloat(formData.monthly_contribution) : null,
        contribution_day: formData.contribution_day,
        notes: formData.notes
      });
      
      toast.success(t('objective_created'));
      setShowCreate(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to create objective:', error);
      toast.error(t('failed_to_create'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!showEdit) return;
    
    setIsSubmitting(true);
    try {
      await updateObjective(showEdit.objective_id, {
        name: formData.name,
        target_amount: parseFloat(formData.target_amount),
        target_date: new Date(formData.target_date).toISOString(),
        icon: formData.icon,
        color: formData.color,
        budget_type: formData.budget_type,
        auto_contribute: formData.auto_contribute,
        monthly_contribution: formData.auto_contribute ? parseFloat(formData.monthly_contribution) : null,
        contribution_day: formData.contribution_day,
        notes: formData.notes
      });
      
      toast.success(t('objective_updated'));
      setShowEdit(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to update objective:', error);
      toast.error(t('failed_to_update'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePause = async (obj) => {
    try {
      await updateObjective(obj.objective_id, { is_paused: !obj.is_paused });
      toast.success(
        obj.is_paused 
          ? t('objective_reactivated')
          : t('objective_paused')
      );
      loadData();
    } catch (error) {
      console.error('Failed to toggle pause:', error);
    }
  };

  const handleContribute = async () => {
    if (!showContribute || !contributeAmount) return;
    
    setIsSubmitting(true);
    try {
      const response = await contributeToObjective(showContribute.objective_id, parseFloat(contributeAmount));
      toast.success(response.data.message);
      setShowContribute(null);
      setContributeAmount('');
      loadData();
    } catch (error) {
      console.error('Failed to contribute:', error);
      toast.error(error.response?.data?.detail || (t('error')));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverride = async () => {
    if (!showOverride) return;
    
    setIsSubmitting(true);
    try {
      const response = await overrideObjectiveContribution(
        showOverride.objective_id, 
        overrideAmount ? parseFloat(overrideAmount) : null
      );
      toast.success(response.data.message);
      setShowOverride(null);
      setOverrideAmount('');
      loadData();
    } catch (error) {
      console.error('Failed to override:', error);
      toast.error(error.response?.data?.detail || (t('error')));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (objectiveId) => {
    try {
      await deleteObjective(objectiveId);
      toast.success(t('objective_deleted'));
      setShowDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete objective:', error);
      toast.error(t('failed_to_delete'));
    }
  };

  const getIconComponent = (iconId) => {
    const iconDef = OBJECTIVE_ICONS.find(i => i.id === iconId);
    if (!iconDef) return <Target className="w-6 h-6" />;
    if (typeof iconDef.icon === 'string') return <span className="text-2xl">{iconDef.icon}</span>;
    const IconComp = iconDef.icon;
    return <IconComp className="w-6 h-6" />;
  };

  // Summary stats
  const totalTarget = objectives.reduce((sum, obj) => sum + (obj.target_amount || 0), 0);
  const totalSaved = objectives.reduce((sum, obj) => sum + (obj.current_amount || 0), 0);
  const completedCount = objectives.filter(obj => obj.is_completed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 pb-24 max-w-4xl" data-testid="objectives-page">
      {/* Header */}
      <div className="mb-6 space-y-4">
        {/* Title row */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">
              {t('my_objectives')}
            </h1>
            <p className="text-muted-foreground text-sm truncate">
              {t('save_for_projects')}
            </p>
          </div>
        </div>
        {/* Actions row */}
        <div className="flex items-center justify-end gap-3">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => setShowStats(true)}
            title={t('statistics')}
            data-testid="stats-btn"
          >
            <BarChart3 className="w-4 h-4" />
          </Button>
          <Button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="create-objective-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('create')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('total_target')}
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(totalTarget)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('saved')}
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalSaved)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Check className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('completed_plural')}
                </p>
                <p className="text-2xl font-bold">
                  {completedCount}/{objectives.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Objectives List */}
      <div className="space-y-4">
        {objectives.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                {t('no_objectives_yet')}
              </p>
              <Button onClick={() => { resetForm(); setShowCreate(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                {t('create_first_objective')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <AnimatePresence>
            {objectives.map((obj) => {
              const progress = (obj.current_amount / obj.target_amount) * 100;
              const isOwn = obj.is_own !== false;
              
              return (
                <motion.div
                  key={obj.objective_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                >
                  <Card className={`${obj.is_paused ? 'opacity-60' : ''} ${obj.is_completed ? 'border-green-300 bg-green-50/50' : ''}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${obj.color}20` }}
                          >
                            {getIconComponent(obj.icon)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`font-semibold ${obj.is_paused ? 'line-through' : ''}`}>
                                {obj.name}
                              </h3>
                              {/* Show owner name for family objectives not owned by current user */}
                              {!isOwn && obj.owner_name && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {obj.owner_name}
                                </span>
                              )}
                              {obj.budget_type === 'common' && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {t('shared_label')}
                                </span>
                              )}
                              {obj.is_paused && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                  {t('paused')}
                                </span>
                              )}
                              {obj.is_completed && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Check className="w-3 h-3" />
                                  {t('achieved_exclaim')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {obj.days_remaining > 0 
                                ? `${obj.days_remaining} ${t('days_remaining')}`
                                : (t('deadline_passed'))
                              }
                            </p>
                          </div>
                        </div>
                        
                        {isOwn && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTogglePause(obj)}
                              title={obj.is_paused ? 'Réactiver' : 'Mettre en pause'}
                            >
                              {obj.is_paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(obj)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => setShowDeleteConfirm(obj.objective_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Progress */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-medium">{formatCurrency(obj.current_amount)}</span>
                          <span className="text-muted-foreground">{formatCurrency(obj.target_amount)}</span>
                        </div>
                        <Progress value={Math.min(100, progress)} className="h-3" />
                        <p className="text-xs text-muted-foreground mt-1 text-right">
                          {progress.toFixed(0)}%
                        </p>
                      </div>

                      {/* Auto-contribute info */}
                      {obj.auto_contribute && (
                        <div className="bg-muted/50 rounded-lg p-3 mb-4 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <RefreshCw className="w-4 h-4 text-primary" />
                              <span>
                                {t('auto_contribute_info')
                                  .replace('{amount}', formatCurrency(obj.next_contribution_override || obj.monthly_contribution))
                                  .replace('{day}', obj.contribution_day)}
                              </span>
                              {obj.next_contribution_override && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                  {t('modified_this_month')}
                                </span>
                              )}
                            </div>
                            {isOwn && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setOverrideAmount(obj.next_contribution_override?.toString() || '');
                                  setShowOverride(obj);
                                }}
                              >
                                {t('change_label')}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Suggestion to enable auto-contribute */}
                      {!obj.auto_contribute && !obj.is_completed && !obj.is_paused && isOwn && obj.days_remaining > 0 && (
                        <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-3 mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-primary" />
                              <div>
                                <p className="text-sm font-medium">
                                  {t('suggestion')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t('monthly_to_reach_goal').replace('{amount}', formatCurrency(obj.monthly_required || 0))}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await updateObjective(obj.objective_id, {
                                    auto_contribute: true,
                                    monthly_contribution: Math.ceil(obj.monthly_required || 0),
                                    contribution_day: 1
                                  });
                                  toast.success(t('auto_contribute_enabled'));
                                  loadData();
                                } catch (error) {
                                  toast.error('Erreur');
                                }
                              }}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              {t('enable')}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Required per period */}
                      {!obj.is_completed && obj.days_remaining > 0 && (
                        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                          <div className="bg-muted/50 rounded-lg p-2">
                            <p className="text-xs text-muted-foreground">{t('daily')}</p>
                            <p className="font-semibold text-sm">{formatCurrency(obj.daily_required || 0)}</p>
                          </div>
                          <div className="bg-muted/50 rounded-lg p-2">
                            <p className="text-xs text-muted-foreground">{t('weekly')}</p>
                            <p className="font-semibold text-sm">{formatCurrency(obj.weekly_required || 0)}</p>
                          </div>
                          <div className="bg-muted/50 rounded-lg p-2">
                            <p className="text-xs text-muted-foreground">{t('monthly')}</p>
                            <p className="font-semibold text-sm">{formatCurrency(obj.monthly_required || 0)}</p>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {!obj.is_completed && !obj.is_paused && (
                        // Allow contribution if: own objective OR family member's objective in shared/hybrid mode
                        (isOwn || (family && ['shared', 'hybrid'].includes(family.budget_mode) && user?.family_role !== 'child')) && (
                          <Button 
                            className="w-full"
                            onClick={() => {
                              setContributeAmount('');
                              setShowContribute(obj);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            {language === 'fr' 
                              ? (isOwn ? 'Ajouter une contribution' : `Contribuer à l'objectif de ${obj.owner_name || 'ce membre'}`)
                              : language === 'pt'
                              ? (isOwn ? 'Adicionar contribuição' : `Contribuir para o objetivo de ${obj.owner_name || 'membro'}`)
                              : (isOwn ? 'Add contribution' : `Contribute to ${obj.owner_name || 'member'}'s goal`)
                            }
                          </Button>
                        )
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreate || !!showEdit} onOpenChange={(open) => { if (!open) { setShowCreate(false); setShowEdit(null); resetForm(); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEdit 
                ? (t('edit') + ' ' + t('objective').toLowerCase())
                : t('new_objective')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('name')} <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder={t('example_vacation')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Target Amount */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('target_amount')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.target_amount}
                  onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
              </div>
            </div>

            {/* Target Date */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('target_date')} <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Icon Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('icon')}
              </label>
              <div className="grid grid-cols-5 gap-2">
                {OBJECTIVE_ICONS.map((iconDef) => (
                  <button
                    key={iconDef.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: iconDef.id })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.icon === iconDef.id
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {typeof iconDef.icon === 'string' ? (
                      <span className="text-xl">{iconDef.icon}</span>
                    ) : (
                      <iconDef.icon className="w-5 h-5 mx-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Budget Type (if family) */}
            {family && family.budget_mode === 'hybrid' && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('budget_type')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, budget_type: 'personal' })}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.budget_type === 'personal'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-border'
                    }`}
                  >
                    <Wallet className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">{t('personal')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, budget_type: 'common' })}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.budget_type === 'common'
                        ? 'border-green-500 bg-green-50'
                        : 'border-border'
                    }`}
                  >
                    <Users className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">{t('common')}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Auto-contribute toggle */}
            <div className="border rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-sm">
                      {t('auto_contribute')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('auto_monthly_contribution')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.auto_contribute}
                  onCheckedChange={(checked) => setFormData({ ...formData, auto_contribute: checked })}
                />
              </div>
              
              {formData.auto_contribute && (
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      {t('monthly_amount')}
                    </label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="100"
                        value={formData.monthly_contribution}
                        onChange={(e) => setFormData({ ...formData, monthly_contribution: e.target.value })}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      {t('contribution_day')}
                    </label>
                    <Select 
                      value={formData.contribution_day.toString()} 
                      onValueChange={(v) => setFormData({ ...formData, contribution_day: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 28}, (_, i) => i + 1).map((day) => (
                          <SelectItem key={day} value={day.toString()}>
                            {day === 1 ? t('first_ordinal') : day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('notes')} <span className="text-muted-foreground text-xs">({t('optional')})</span>
              </label>
              <Input
                placeholder={t('example_vacation')}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setShowEdit(null); resetForm(); }}>
              {t('cancel')}
            </Button>
            <Button onClick={showEdit ? handleUpdate : handleCreate} disabled={isSubmitting}>
              {isSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : (showEdit ? (t('save')) : (t('create')))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contribute Modal */}
      <Dialog open={!!showContribute} onOpenChange={() => { setShowContribute(null); setContributeAmount(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t('add_contribution')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              {t('amount')}
            </label>
            <div className="relative">
              <Input
                type="number"
                placeholder="0"
                value={contributeAmount}
                onChange={(e) => setContributeAmount(e.target.value)}
                className="pr-8 text-lg"
                autoFocus
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
            </div>
            {showContribute && (
              <>
                <p className="text-sm text-muted-foreground mt-2">
                  {t('progress')}: {formatCurrency(showContribute.current_amount)} / {formatCurrency(showContribute.target_amount)}
                </p>
                
                {/* Show deduction source info based on budget mode */}
                {family && (
                  <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <Wallet className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">
                        {t('will_be_deducted_from')}
                      </span>
                    </div>
                    <p className="text-sm font-medium mt-1">
                      {family.budget_mode === 'shared' && (
                        <span className="text-green-600">
                          {t('shared_family_budget')}
                        </span>
                      )}
                      {family.budget_mode === 'hybrid' && (
                        <span className="text-blue-600">
                          {t('common_pot_label')}
                        </span>
                      )}
                      {family.budget_mode === 'separate' && (
                        <span className="text-purple-600">
                          {t('your_personal_balance')}
                        </span>
                      )}
                    </p>
                  </div>
                )}
                
                {/* Show owner if contributing to someone else's objective */}
                {showContribute.owner_name && !showContribute.is_own && (
                  <p className="text-sm text-blue-600 mt-2 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {language === 'fr' 
                      ? `Objectif de ${showContribute.owner_name}` 
                      : language === 'pt'
                      ? `Objetivo de ${showContribute.owner_name}`
                      : `${showContribute.owner_name}'s objective`
                    }
                  </p>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowContribute(null); setContributeAmount(''); }}>
              {t('cancel')}
            </Button>
            <Button onClick={handleContribute} disabled={isSubmitting || !contributeAmount}>
              {isSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : (t('add'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Contribution Modal */}
      <Dialog open={!!showOverride} onOpenChange={() => { setShowOverride(null); setOverrideAmount(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t('change_this_month_contribution')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {language === 'fr' 
                ? `Montant prévu: ${formatCurrency(showOverride?.monthly_contribution || 0)}. Vous pouvez le modifier pour ce mois uniquement.`
                : language === 'pt'
                ? `Previsto: ${formatCurrency(showOverride?.monthly_contribution || 0)}. Pode alterar apenas para este mês.`
                : `Planned: ${formatCurrency(showOverride?.monthly_contribution || 0)}. You can change it for this month only.`
              }
            </p>
            <label className="text-sm font-medium mb-2 block">
              {t('new_amount')}
            </label>
            <div className="relative">
              <Input
                type="number"
                placeholder={showOverride?.monthly_contribution?.toString() || '0'}
                value={overrideAmount}
                onChange={(e) => setOverrideAmount(e.target.value)}
                className="pr-8"
                autoFocus
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('leave_empty_default')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowOverride(null); setOverrideAmount(''); }}>
              {t('cancel')}
            </Button>
            <Button onClick={handleOverride} disabled={isSubmitting}>
              {isSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : (t('confirm'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              {t('delete_objective_confirm')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            {t('action_irreversible')}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              {t('cancel')}
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(showDeleteConfirm)}>
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statistics Modal */}
      <Dialog open={showStats} onOpenChange={setShowStats}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              {t('objectives_statistics')}
            </DialogTitle>
          </DialogHeader>
          
          {stats ? (
            <div className="space-y-6">
              {/* General stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                  <p className="text-xs text-muted-foreground mb-1">
                    {t('total_objectives')}
                  </p>
                  <p className="text-2xl font-bold text-blue-600">{stats.total_objectives}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20">
                  <p className="text-xs text-muted-foreground mb-1">
                    {t('completed_plural')}
                  </p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed_objectives}</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20">
                  <p className="text-xs text-muted-foreground mb-1">
                    {t('paused')}
                  </p>
                  <p className="text-2xl font-bold text-amber-600">{stats.paused_objectives}</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950/20">
                  <p className="text-xs text-muted-foreground mb-1">
                    {t('success_rate')}
                  </p>
                  <p className="text-2xl font-bold text-purple-600">{stats.completion_rate}%</p>
                </div>
              </div>

              {/* Savings summary */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">
                    {t('total_savings')}
                  </span>
                </div>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_saved)}</p>
                <p className="text-xs text-muted-foreground">
                  {t('of_target').replace('{amount}', formatCurrency(stats.total_target))}
                </p>
              </div>

              {/* Pause behavior stats */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-medium">
                    {t('pause_behavior')}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-2 rounded-lg border">
                    <p className="text-xs text-muted-foreground">
                      {t('total_pauses')}
                    </p>
                    <p className="text-lg font-semibold">{stats.pause_stats?.total_pauses || 0}</p>
                  </div>
                  <div className="p-2 rounded-lg border">
                    <p className="text-xs text-muted-foreground">
                      {t('days_paused')}
                    </p>
                    <p className="text-lg font-semibold">{stats.pause_stats?.total_paused_days || 0}</p>
                  </div>
                  <div className="p-2 rounded-lg border">
                    <p className="text-xs text-muted-foreground">
                      {t('objectives_paused_1x')}
                    </p>
                    <p className="text-lg font-semibold">{stats.pause_stats?.objectives_paused_at_least_once || 0}</p>
                  </div>
                  <div className="p-2 rounded-lg border">
                    <p className="text-xs text-muted-foreground">
                      {t('avg_duration_days')}
                    </p>
                    <p className="text-lg font-semibold">{stats.pause_stats?.average_pause_duration_days || 0}</p>
                  </div>
                </div>

                {stats.pause_stats?.most_paused_objective && (
                  <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900">
                    <p className="text-xs text-muted-foreground mb-1">
                      {t('most_paused_objective')}
                    </p>
                    <p className="font-medium">{stats.pause_stats.most_paused_objective.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.pause_stats.most_paused_objective.pause_count} {t('pauses')} • 
                      {stats.pause_stats.most_paused_objective.total_paused_days} {t('days')}
                    </p>
                  </div>
                )}
              </div>

              {/* Savings Evolution Chart */}
              {stats.savings_history && stats.savings_history.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium">
                      {t('savings_evolution')}
                    </span>
                  </div>
                  
                  <div className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={stats.savings_history}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          dataKey={language === 'fr' ? 'month_fr' : language === 'pt' ? 'month_pt' : 'month_en'} 
                          tick={{ fontSize: 11 }}
                          className="text-muted-foreground"
                        />
                        <YAxis 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => `${value}€`}
                          className="text-muted-foreground"
                        />
                        <Tooltip 
                          content={<SavingsChartTooltip language={language} />}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="cumulative" 
                          stroke="#10b981" 
                          fillOpacity={1} 
                          fill="url(#colorSavings)" 
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowStats(false)}>
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
